import { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { format } from 'date-fns';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { ModalButtonWithForm } from 'components/common';
import { ProductCustomAttributes } from 'components/common/customAttributes';
import { CertificatesSelectTemplate } from 'components/admin/settings/certificates';
import { DatepickerWithTime, FormGroup, Text } from 'components/common/form';

import type { FormApi } from 'informed';
import type { CalendarEvent } from 'shared-types';

type FormState = {
  CertificateTemplateId?: number;
  customAttributes: CalendarEvent['customAttributes'];
  title: CalendarEvent['title'];
  startDate: number;
  endDate: number;
};

interface Props {
  getRef?: (el: HTMLButtonElement) => void;
  initialDate: Date;
  onClose: () => void;
}

const CalendarAddEvent = ({ getRef, initialDate, onClose }: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    serverConfig: {
      calendarEventCustomAttributes,
      certificatesEnableSelection,
      certificatesEnableSelectionOnCreate
    }
  } = useReduxState();

  const formApi = useRef<FormApi<FormState>>(undefined);

  const router = useRouter();

  const [startDate, setStartDate] = useState<number>(
    Date.parse(`${format(initialDate, 'y-MM-d')} 08:00:00`)
  );

  const getAddEventRef = (el: HTMLButtonElement) => {
    if (!getRef) return null;
    getRef(el);
  };

  useEffect(() => {
    setStartDate(Date.parse(`${format(initialDate, 'y-MM-d')} 08:00:00`));
  }, [initialDate]);

  const handleStartDateChange = (value: number) => {
    const timeDiff = value - startDate;

    const endDate: number = formApi.current.getValue('endDate');
    formApi.current.setValue('endDate', endDate + timeDiff);

    setStartDate(value);
  };

  const handleFormChange = ({ startDate, endDate }: FormState) => {
    if (endDate < startDate) {
      formApi.current.setError('endDate', messages.eventCalendarDatesError);
    } else formApi.current.setError('endDate', undefined);
  };

  const handleSubmit = async (values: FormState) => {
    const startDate = new Date(values.startDate).toISOString();
    const endDate = new Date(values.endDate).toISOString();

    try {
      const response = await api.post('/admin/calendar-events', {
        CertificateTemplateId: values.CertificateTemplateId || null,
        customAttributes: values.customAttributes,
        title: values.title,
        startDate,
        endDate
      });

      dispatch(
        actions.addNotice({
          text: messages.eventCalendarAddEventSuccess,
          type: 'success'
        })
      );
      router.push(`/calendar/${response.data.eventId}/settings`);
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const validateTitle = (value: FormState['title']) => {
    return !value?.trim() ? messages.commonTitleEmpty : undefined;
  };

  return (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon="plus"
      buttonText={messages.eventCalendarAddEvent}
      formOnValueChange={handleFormChange}
      initialValues={{ CertificateTemplateId: 0 }}
      modalActionText={messages.commonCreate}
      modalTitle={messages.eventCalendarAddEvent}
      onHide={onClose}
      onSubmit={handleSubmit}
      pristineIsValid={false}
      setFormApi={(api: FormApi<FormState>) => (formApi.current = api)}
      setRef={getAddEventRef}
    >
      {() => (
        <>
          <FormGroup label={messages.commonTitle}>
            <Text
              field="title"
              id="title"
              required={true}
              validate={validateTitle}
              validateOnChange={true}
            />
          </FormGroup>

          {certificatesEnableSelection &&
            certificatesEnableSelectionOnCreate && (
              <CertificatesSelectTemplate type="calendar-event" />
            )}

          <FormGroup label={messages.publishStartDate}>
            <DatepickerWithTime
              field="startDate"
              id="startDate"
              initialValue={Date.parse(
                `${format(initialDate, 'y-MM-d')} 08:00:00`
              )}
              isClearable={false}
              onChange={handleStartDateChange}
              style={{ margin: 0, padding: 0 }}
              usePortal={true}
            />
          </FormGroup>

          <FormGroup label={messages.publishEndDate}>
            <DatepickerWithTime
              field="endDate"
              id="endDate"
              initialValue={Date.parse(
                `${format(initialDate, 'y-MM-d')} 10:00:00`
              )}
              isClearable={false}
              minDate={startDate}
              style={{ margin: 0, padding: 0 }}
              usePortal={true}
            />
          </FormGroup>

          {calendarEventCustomAttributes?.some(
            (attr) => attr.required || attr.displayInCreateForm
          ) ? (
            <div style={{ marginTop: 30 }}>
              <ProductCustomAttributes
                hideWarnings={true}
                renderOnlyRequiredOrDisplayable={true}
                type="calendarEvent"
              />
            </div>
          ) : null}
        </>
      )}
    </ModalButtonWithForm>
  );
};

export default CalendarAddEvent;
